import { PortfolioAllocation, PortfolioAllocationV2 } from '@inyova/models';

import { Action, createReducer, on } from '@ngrx/store';
import * as AppRootActions from '../../../app.actions';
import * as ProfileActions from '../../profile/profile.actions';
import * as StrategyActions from '../strategy.actions';

import { AllocationTabs, NotificationTypes, StrategyState } from '@shared/models/Strategy';

export const STRATEGY_REDUCER_FEATURE_KEY = 'strategy';

export const initialState: StrategyState = {
  isStrategyInitialised: false,
  tabIndex: 1,
  showGuarantee: false,
  changeStrategyDisabled: false,
  topicsData: [],
  customerTopicsData: [],
  criteriaData: [],
  customerCriteriaData: [],
  companyDetailData: {
    company: {
      additional_topics: [],
      bad_bullet_points: [],
      based: '',
      criteria_dismatch: [],
      desc: '',
      extra_desc: '',
      good_bullet_points: [],
      id: 0,
      sector: '',
      img: '',
      is_balance: false,
      is_blacklist: false,
      is_wishlist: false,
      is_yellow: false,
      link_string: '',
      link: '',
      logo: '',
      name: '',
      topics: [
        {
          desc: '',
          icon: '',
          id: 0,
          name: '',
          score: 0
        }
      ],
      stock_ratio: 0,
      yova_match: 0
    },
    loading: true
  },
  companyList: {
    blacklist: [],
    wishlist: [],
    yova_stocks: []
  },
  portfolioCompanyList: {
    female_leadership: 0,
    blacklist: [],
    wishlist: [],
    yova_stocks: [],
    broken_pf: false
  },
  brokenPortfolio: {
    showWarningModal: false,
    showUndoMessage: false,
    doModalCheck: false,
    doUndoCheck: false
  },
  customerInvestmentData: {
    bonds: 0,
    currency: 'CHF',
    current_amount: 0,
    monthly_investment: 0,
    pure_impact: false,
    pure_stock: false,
    stock_split: 0,
    live_stock_split: 0,
    stocks: 0,
    total: 0
  },
  notification: {
    company: '',
    type: '',
    show: false
  },
  customerDiscardedWish: [],
  wasStrategyChanged: {
    strategyWasChanged: false,
    redirectUrl: '',
    modalOpened: false
  },
  dialogDiscardedWishes: {
    discardedWishesDialog: false,
    redirectUrl: '',
    modalOpened: false
  },
  stockSplit: {
    riskStockSplit: null,
    maxStockSplit: null
  },
  searchCompanies: [],
  searchCompaniesLoading: false,
  stockAllocation: {
    currency_percentages: [],
    sector_percentages: [],
    currentTab: 'sector',
    selectedIndex: null
  },
  portfolioAllocation: null,
  portfolioAllocationV2: null,
  accountReportHasData: false,
  interviewWidget: {
    showNotification: false,
    showWidget: false
  },
  qlOnboardingStep: 1,
  investmentPlan: {
    account_id: '',
    initial_investment: 10000,
    monthly_investment: 500,
    stock_split: 0
  },
  strategyInfo: {
    live_stock_split: 0,
    fund_amount: 0,
    bond_amount: 0,
    total_invested_amount: 0,
    monthly_investment: 0
  },
  stocks: [],
  stockAllocationEve2: {
    sector_weights: {},
    currency_weights: {},
    currentTab: 'sector',
    selectedIndex: null
  },
  isEditModeEve2: false,
  riskSurveyFormData: null
};

const strategyReducer = createReducer(
  initialState,
  on(StrategyActions.fetchStrategyEditModeSuccess, StrategyActions.updateStrategyEditModeSuccess, (state, action) => ({
    ...state,
    changeStrategyDisabled: action.changeStrategyDisabled
  })),
  on(StrategyActions.discardStrategyChangesSuccess, (state) => ({
    ...state,
    changeStrategyDisabled: false
  })),
  on(StrategyActions.getTopicsDataSuccess, (state, action) => ({
    ...state,
    topicsData: action.topics,
    customerTopicsData: action.customer
  })),
  on(StrategyActions.getCriteriaDataSuccess, (state, action) => ({
    ...state,
    criteriaData: action.criteria,
    customerCriteriaData: action.customer
  })),
  on(StrategyActions.getCompanyDetailSuccess, (state, action) => ({
    ...state,
    companyDetailData: {
      company: action.company,
      loading: action.loading
    }
  })),
  on(StrategyActions.emptyCompanyDetail, (state) => {
    return {
      ...state,
      ...(state.companyDetailData.company.id !== 0 && { companyDetailData: { ...initialState.companyDetailData } })
    };
  }),
  on(StrategyActions.setCompanyDetailLoading, (state) => ({
    ...state,
    companyDetailData: {
      ...state.companyDetailData,
      loading: true
    }
  })),
  on(StrategyActions.getCompanyListSuccess, (state, action) => ({
    ...state,
    companyList: action
  })),
  on(StrategyActions.updatePortfolioCompanyList, (state) => ({
    ...state,
    ...(!state.portfolioCompanyList.broken_pf && {
      brokenPortfolio: {
        ...state.brokenPortfolio,
        doModalCheck: true
      }
    })
  })),
  on(StrategyActions.getPortfolioCompanyListSuccess, (state, action) => ({
    ...state,
    portfolioCompanyList: action,
    isStrategyInitialised: true,
    // hide success notification if the portfolio is broken
    ...(!state.portfolioCompanyList.broken_pf && action.broken_pf && { notification: { company: '', type: '' as NotificationTypes, show: false } }),
    // wishlist or exclude button has been clicked, display broken_pf modal based response
    ...(state.brokenPortfolio.doModalCheck && {
      brokenPortfolio: { ...state.brokenPortfolio, showWarningModal: action.broken_pf, doModalCheck: false }
    }),
    // broken_pf dialog undo success message
    ...(state.brokenPortfolio.doUndoCheck &&
      /*eslint-disable */
      !action.broken_pf && {
        brokenPortfolio: { showWarningModal: false, showUndoMessage: true, doUndoCheck: false, doModalCheck: false }
      })
    /*eslint-enable */
  })),
  on(StrategyActions.hideBrokenPortfolioWarning, (state) => ({
    ...state,
    brokenPortfolio: {
      ...state.brokenPortfolio,
      showWarningModal: false
    }
  })),
  on(StrategyActions.hideBrokenPortfolioUndoMessage, (state) => ({
    ...state,
    brokenPortfolio: {
      ...state.brokenPortfolio,
      showUndoMessage: false
    }
  })),
  on(StrategyActions.setTabIndex, (state, action) => ({
    ...state,
    tabIndex: action.tabIndex
  })),
  on(StrategyActions.setGuaranteeVisibility, (state, action) => ({
    ...state,
    showGuarantee: action.value
  })),
  on(StrategyActions.getCustomerInvestmentSuccess, (state, action) => ({
    ...state,
    customerInvestmentData: action.investmentData
  })),
  on(StrategyActions.setNotification, (state, action) => ({
    ...state,
    notification: action.notification
  })),
  on(StrategyActions.disableInterviewNotification, (state) => ({
    ...state,
    interviewWidget: {
      ...state.interviewWidget,
      showNotification: false
    }
  })),
  on(StrategyActions.disableInterviewNotificationSuccess, (state, action) => ({
    ...state,
    interviewWidget: {
      ...state.interviewWidget,
      showWidget: action.showWidget
    }
  })),
  on(StrategyActions.disableInterviewNotificationFailed, (state) => ({
    ...state
  })),
  on(StrategyActions.getRiskStockSplitSuccess, (state, action) => ({
    ...state,
    stockSplit: {
      riskStockSplit: action.riskStockSplit,
      maxStockSplit: action.maxStockSplit
    }
  })),
  on(StrategyActions.setNotificationShowStatus, (state, action) => ({
    ...state,
    notification: { ...state.notification, show: action.show }
  })),
  on(StrategyActions.getCustomerDiscardedWishesSuccess, (state, action) => ({
    ...state,
    customerDiscardedWish: action.discardedWishes
  })),
  on(StrategyActions.setStrategyWasChanged, StrategyActions.setStrategyWasChangedEve2, (state, action) => ({
    ...state,
    // redirectUrl and modalOpened are not needed for EVE2
    wasStrategyChanged: { modalOpened: false, redirectUrl: '', ...action }
  })),
  on(StrategyActions.undoStrategyActions, (state, action) => ({
    ...state,
    ...(action.source === 'dialog' && { brokenPortfolio: { ...state.brokenPortfolio, doUndoCheck: true } })
  })),
  on(StrategyActions.setDiscardedWishesDialog, (state, action) => ({
    ...state,
    dialogDiscardedWishes: action
  })),
  on(StrategyActions.fetchStockAllocationsSuccess, (state, action) => ({
    ...state,
    stockAllocation: {
      ...action.stockAllocation,
      selectedIndex: null,
      currentTab: 'sector' as AllocationTabs
    }
  })),
  on(StrategyActions.fetchStockAllocationEve2Success, (state, action) => ({
    ...state,
    stockAllocationEve2: {
      ...action.stockAllocation,
      selectedIndex: null,
      currentTab: 'sector' as AllocationTabs
    }
  })),
  on(StrategyActions.setStockAllocationDetails, (state, action) => ({
    ...state,
    stockAllocation: {
      ...state.stockAllocation,
      selectedIndex: action.selectedIndex,
      currentTab: action.currentTab
    }
  })),
  on(StrategyActions.getWithdrawalDataSuccess, (state, { data, accountReportHasData, hasFeatureFlag }) => ({
    ...state,
    portfolioAllocation: !hasFeatureFlag ? (data as PortfolioAllocation) : null,
    portfolioAllocationV2: hasFeatureFlag ? (data as PortfolioAllocationV2) : null,
    accountReportHasData
  })),
  on(StrategyActions.getWithdrawalDataFail, (state) => ({
    ...state,
    portfolioAllocation: null,
    portfolioAllocationV2: null,
    accountReportHasData: false
  })),
  on(StrategyActions.fetchSearchCompanies, (state) => ({
    ...state,
    searchCompaniesLoading: true
  })),
  on(StrategyActions.fetchSearchCompaniesSuccess, (state, action) => ({
    ...state,
    searchCompanies: action.searchCompanies,
    searchCompaniesLoading: false
  })),
  on(StrategyActions.fetchSearchCompaniesFail, (state) => ({
    ...state,
    searchCompaniesLoading: false
  })),
  on(AppRootActions.switchAccount, ProfileActions.logout, () => ({ ...initialState })),
  on(StrategyActions.updateQLOnboardingStep, (state, action) => ({
    ...state,
    tabIndex: action.step
  })),
  on(StrategyActions.updateQLOnboardingStepSuccess, (state, action) => ({
    ...state,
    qlOnboardingStep: action.step
  })),
  on(
    StrategyActions.updateInvestmentPlanSuccess,
    (state, action): StrategyState => ({
      ...state,
      investmentPlan: {
        ...state.investmentPlan,
        initial_investment: action.investmentPlan.initial_investment,
        monthly_investment: action.investmentPlan.monthly_investment,
        stock_split: action.investmentPlan.stock_split
      }
    })
  ),
  on(
    StrategyActions.getInvestmentPlanSuccess,
    (state, action): StrategyState => ({
      ...state,
      investmentPlan: {
        ...state.investmentPlan,
        initial_investment: action.initial_investment,
        monthly_investment: action.monthly_investment,
        stock_split: action.stock_split
      }
    })
  ),
  on(
    StrategyActions.getStrategyInfoSuccess,
    (state, action): StrategyState => ({
      ...state,
      strategyInfo: {
        ...state.strategyInfo,
        total_invested_amount: action.total_invested_amount,
        monthly_investment: action.monthly_investment,
        live_stock_split: action.live_stock_split
      }
    })
  ),
  on(
    StrategyActions.getFundBreakdownSuccess,
    (state, action): StrategyState => ({
      ...state,
      stocks: action.stocks,
      isStrategyInitialised: true
    })
  ),
  on(StrategyActions.getFundBreakdownFail, (state): StrategyState => ({ ...state, isStrategyInitialised: false })),
  on(AppRootActions.setIsLanguageChange, (state): StrategyState => ({ ...state, isStrategyInitialised: false, stocks: [] })),
  on(
    StrategyActions.getInvestmentDetailsSuccess,
    (state, action): StrategyState => ({
      ...state,
      changeStrategyDisabled: action.strategy_change_available === false
    })
  ),
  on(StrategyActions.saveRiskFormDataEve2, (state, action) => ({
    ...state,
    riskSurveyFormData: action
  })),
  on(StrategyActions.enterStrategyEditModeEve2, (state) => ({
    ...state,
    isEditModeEve2: true
  })),
  on(StrategyActions.saveStrategyChangesSuccessEve2, StrategyActions.discardStrategyChangesEve2, (state) => ({
    ...state,
    isEditModeEve2: false,
    riskSurveyFormData: null
  })),
  on(
    StrategyActions.saveStrategyChangesSuccessEve2,
    StrategyActions.discardStrategyChangesEve2,
    StrategyActions.updateRiskRecheckSuccessEve2,
    StrategyActions.discardRiskRecheckEve2,
    (state) => ({
      ...state,
      wasStrategyChanged: initialState.wasStrategyChanged
    })
  )
);

export function reducer(state: StrategyState | undefined, action: Action): StrategyState {
  return strategyReducer(state, action);
}
